<template>
  <div class="grid-container table">
    <div class="grid-x">
      <div class="small-4 cell client-names">
        <ul class="client-names-list">
          <li
            v-for="(name, n) in clientNames"
            :key="n"
          >
            <span
              v-if="displayRankBadge({ client: name, position: n })"
              class="rankBadge"
              :style="{ backgroundColor: currentColor }"
            >
              {{ n + 1 }}
            </span>
            {{ formatClientName({ client: name, position: n }) }}
          </li>
        </ul>
      </div>
      <div
        class="small-2 cell base-data"
        :class="{ selectedOrderBG: tableSortColumn === 'base' }"
      >
        <rare-3-YOY-sort-button
          :label-from="dateLabels.fromBase"
          :label-to="dateLabels.toBase"
          :button-name="'base'"
        />
        <ul class="data-list">
          <li
            v-for="(value, key) in baseData"
            :key="key"
            :class="{
              greyNA: formatData(value) === 'N/A',
              industryAverage: isIndustryAverage(key),
            }"
          >
            {{ formatData(value) }}
          </li>
        </ul>
      </div>
      <div
        class="small-2 cell prev-1-data"
        :class="{ selectedOrderBG: tableSortColumn === 'prevYear1' } "
      >
        <rare-3-YOY-sort-button
          :label-from="dateLabels.fromPrev1"
          :label-to="dateLabels.toPrev1"
          :button-name="'prevYear1'"
        />
        <ul class="data-list">
          <li
            v-for="(value, key) in prevYear1Data"
            :key="key"
            :class="{
              greyNA: formatData(value) === 'N/A',
              industryAverage: isIndustryAverage(key),
            }"
          >
            {{ formatData(value) }}
          </li>
        </ul>
      </div>
      <div
        class="small-2 cell prev-2-data"
        :class="{ selectedOrderBG: tableSortColumn === 'prevYear2' }"
      >
        <rare-3-YOY-sort-button
          :label-from="dateLabels.fromPrev2"
          :label-to="dateLabels.toPrev2"
          :button-name="'prevYear2'"
        />
        <ul class="data-list">
          <li
            v-for="(value, key) in prevYear2Data"
            :key="key"
            :class="{
              greyNA: formatData(value) === 'N/A',
              industryAverage: isIndustryAverage(key),
            }"
          >
            {{ formatData(value) }}
          </li>
        </ul>
      </div>
      <div
        class="small-2 cell prev-3-data"
        :class="{ selectedOrderBG: tableSortColumn === 'prevYear3' } "
      >
        <rare-3-YOY-sort-button
          :label-from="dateLabels.fromPrev3"
          :label-to="dateLabels.toPrev3"
          :button-name="'prevYear3'"
        />
        <ul class="data-list">
          <li
            v-for="(value, key) in prevYear3Data"
            :key="key"
            :class="{
              greyNA: formatData(value) === 'N/A',
              industryAverage: isIndustryAverage(key),
            }"
          >
            {{ formatData(value) }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import Rare3YOYsortButton from '../controls/Rare3YOYsortButton.vue';

export default {
  name: 'RareIndustryOverview3YOYTable',
  components: {
    'rare-3-YOY-sort-button': Rare3YOYsortButton,
  },
  props: {
    nameInLabels: {
      type: String,
      required: false,
      default: '',
    },
    clientNames: {
      type: Array,
      required: true,
    },
    baseData: {
      type: Array,
      required: true,
    },
    prevYear1Data: {
      type: Array,
      required: true,
    },
    prevYear2Data: {
      type: Array,
      required: true,
    },
    prevYear3Data: {
      type: Array,
      required: true,
    },

  },
  data() {
    return {
      blue: this.$store.getters.fetchTableTextColor('base'),
      green: this.$store.getters.fetchTableTextColor('prevYear1'),
      pink: this.$store.getters.fetchTableTextColor('prevYear2'),
      orange: this.$store.getters.fetchTableTextColor('prevYear3'),
      dateLabels: {
        fromBase: '',
        toBase: '',
        fromPrev1: '',
        toPrev1: '',
        fromPrev2: '',
        toPrev2: '',
        fromPrev3: '',
        toPrev3: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      rareMode: 'rareMode',
      tableSortColumn: 'tableSortColumn',
      fromDate: 'fromDate',
      toDate: 'toDate',
    }),
    currentColor() {
      return this.$store.getters.fetchTableTextColor(this.tableSortColumn);
    },
  },
  updated() {
    this.$store.commit('updateRankTotal', this.baseData.length);
  },
  mounted() {
    this.dateLabels.fromBase = this.moment(this.fromDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
    this.dateLabels.toBase = this.moment(this.toDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
    this.dateLabels.fromPrev1 = this.moment(this.fromDate, 'YYYY-MM-DD').subtract(1, 'years').format('DD/MM/YYYY');
    this.dateLabels.toPrev1 = this.moment(this.toDate, 'YYYY-MM-DD').subtract(1, 'years').format('DD/MM/YYYY');
    this.dateLabels.fromPrev2 = this.moment(this.fromDate, 'YYYY-MM-DD').subtract(2, 'years').format('DD/MM/YYYY');
    this.dateLabels.toPrev2 = this.moment(this.toDate, 'YYYY-MM-DD').subtract(2, 'years').format('DD/MM/YYYY');
    this.dateLabels.fromPrev3 = this.moment(this.fromDate, 'YYYY-MM-DD').subtract(3, 'years').format('DD/MM/YYYY');
    this.dateLabels.toPrev3 = this.moment(this.toDate, 'YYYY-MM-DD').subtract(3, 'years').format('DD/MM/YYYY');
  },
  methods: {
    displayRankBadge(obj) {
      const { client } = obj;
      const { position } = obj;
      const sortColumn = this.tableSortColumn;
      const value = this[`${sortColumn}Data`][position];

      if (client === this.nameInLabels && !this.rareMode) {
        this.$store.commit('updateRankPosition', position);
      }
      return (obj.client === this.nameInLabels && !this.rareMode && value !== -1);
    },
    formatClientName(obj) {
      const { client } = obj;

      let returnValue;
      const currentClientDisplayName = this.$store.getters.getDisplayNameFromUsername(
        this.$store.getters.currentClient,
      );
      const { currentClient } = this.$store.getters;
      if (this.rareMode || client.includes('Industry')) {
        returnValue = this.$store.getters.getDisplayNameFromUsername(client);
      } else if (!this.rareMode && client === currentClient) {
        returnValue = `${currentClientDisplayName}`;
      } else {
        returnValue = `${client}`;
      }

      if (client === 'industry_average') {
        returnValue = 'Industry Average';
      }

      return returnValue;
    },
    formatData(string) {
      let returnValue;
      if (string === -1) {
        returnValue = 'N/A';
      } else if (string === 'N/A') {
        returnValue = string;
      } else {
        const percentage = (this.$store.getters.dataFormat === 'percentage' ? '%' : '');
        returnValue = `${string}${percentage}`;
      }
      return returnValue;
    },
    isIndustryAverage(key) {
      return ((this.clientNames[key].includes('ndustry')));
    },
  },
};
</script>

<style lang="scss" scoped>

  @import "~@/css/global.scss";

  .table {
    margin-top: 30px;
    padding-right: 0;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 20px 0 0 0;
  }

  li {
    display: block;
    font-size: 14px;
    font-weight: bold;
    font-style: normal;
    line-height: 2.14;
    letter-spacing: 1px;
    text-align: center;
    margin: 0 0 6px 0;
  }

  .selectedOrderBG {
    background-color: #efefef;
  }

  .base-data {
    border-left: 1px solid $graph-grey;
  }

  .prev-1-data {
    border-left: 1px solid $graph-grey;
  }

  .prev-2-data {
    border-left: 1px solid $graph-grey;
  }

  .prev-3-data {
    border-left: 1px solid $graph-grey;
    border-right: 1px solid $graph-grey;
  }

  .client-names-list {
    margin-top: 100px;
  }

  .client-names-list li {
    text-align: left;
    color: $dark-text;
    margin-left: 30px;
    position: relative;
  }

  .data-list li {
    color: $graph-blue-text
  }

  .prev-1-data li {
    color: $graph-green-text
  }

  .prev-2-data li {
    color: $graph-pink-text;
  }

  .prev-3-data li {
    color: $graph-orange-text;
  }

  .rankBadge{
    border-bottom: none !important;
    color: #ffffff !important;
    height: 30px !important;
    width: 30px !important;
    border-radius: 15px !important;
    display:inline-block !important;
    text-align: center !important;
    line-height: 30px !important;
    margin-left: 0px !important;
    position: absolute;
    left: -40px;
    top: 0px;
  }

  .greyNA {
    color: #cecece !important;
  }

  .industryAverage {
    color: #4E5A63 !important;
  }

</style>
